import { z } from "zod";

/* ──────────────────────────────────────────────────────────────────────
   Zod schemas mirror the FastAPI pydantic models. Runtime-validated so any
   contract drift surfaces immediately instead of corrupting React state.
   ────────────────────────────────────────────────────────────────────── */

export const TokenPair = z.object({
  access_token: z.string().min(20),
  refresh_token: z.string().min(20),
  token_type: z.literal("Bearer").or(z.string()),
  expires_in: z.number().int().positive(),
  must_change_password: z.boolean(),
});
export type TokenPair = z.infer<typeof TokenPair>;

export const PermissionEntry = z.object({
  page: z.string(),
  feature: z.string().nullable(),
  show: z.boolean(),
  save: z.boolean(),
  update: z.boolean(),
  delete: z.boolean(),
  print: z.boolean(),
  export: z.boolean(),
});
export type PermissionEntry = z.infer<typeof PermissionEntry>;

export const MeResponse = z.object({
  user_id: z.number().int(),
  username: z.string(),
  email: z.string().nullable(),
  group_id: z.number().int(),
  group_name: z.string(),
  people_id: z.number().int().nullable(),
  must_change_password: z.boolean(),
  last_login_utc: z.string().nullable(),
  permissions: z.array(PermissionEntry),
  image_source_mode: z.enum(["cdn", "proxy"]),
  image_cdn_base: z.string(),
});
export type MeResponse = z.infer<typeof MeResponse>;

export const HealthResponse = z.object({
  ok: z.boolean(),
  app: z.string(),
  version: z.string(),
  db: z.record(z.unknown()),
});
export type HealthResponse = z.infer<typeof HealthResponse>;

export const LoginRequest = z.object({
  username: z.string().min(2),
  password: z.string().min(1),
});
export type LoginRequest = z.infer<typeof LoginRequest>;

export const ChangePasswordRequest = z.object({
  current_password: z.string().min(1),
  new_password: z.string().min(10),
});
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequest>;

/** A FastAPI error envelope. */
export const ApiError = z.object({
  detail: z.union([z.string(), z.array(z.unknown())]).optional(),
});
export type ApiError = z.infer<typeof ApiError>;
